// Per-product detail page. Shows the editable product info on top, then a
// list of all its lots underneath (each editable inline), with a form at the
// bottom for adding a new lot to this product.
//
// URL is /products/:id where :id is the product_id.

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import type { InventoryLot, Product } from "../types";

// ---------- date helpers (mirrors Inventory.tsx) ----------

function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatBestBefore(d: string | null): string {
  if (!d) return "No expiry";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = parseLocalDate(d);
  const days = Math.round(
    (target.getTime() - today.getTime()) / 86400000
  );
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days <= 7) return `In ${days}d`;
  return target.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ---------- main page ----------

export function ItemDetail() {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<{
    locations: string[];
    categories: string[];
  }>({ locations: [], categories: [] });

  // Editable product fields, mirrored from the loaded product.
  const [productName, setProductName] = useState("");
  const [productBrand, setProductBrand] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [productImage, setProductImage] = useState("");
  const [productMinQty, setProductMinQty] = useState("1");
  const [productStarred, setProductStarred] = useState(false);

  const [addingToList, setAddingToList] = useState(false);
  const [addedToList, setAddedToList] = useState(false);

  useEffect(() => {
    if (!productId) return;
    void load();
    api.getSuggestions().then(setSuggestions, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const { product, lots } = await api.getProduct(productId!);
      setProduct(product);
      setLots(lots);
      setProductName(product.name);
      setProductBrand(product.brand ?? "");
      setProductCategory(product.category ?? "");
      setProductImage(product.image_url ?? "");
      setProductMinQty(String(product.min_quantity ?? 1));
      setProductStarred(!!product.is_starred);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  async function onSaveProduct() {
    if (!product) return;
    setSaving(true);
    setErr(null);
    try {
      await api.updateProduct(product.id, {
        name: productName.trim() || product.name,
        brand: productBrand || null,
        category: productCategory || null,
        image_url: productImage || null,
        // `|| 1` would coerce a deliberate 0 (= disable reminders) back to 1.
        // Only fall back to 1 when the input is genuinely empty / NaN.
        min_quantity: Number.isFinite(parseFloat(productMinQty))
          ? parseFloat(productMinQty)
          : 1,
        is_starred: productStarred,
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onAddToList() {
    if (!product) return;
    setAddingToList(true);
    setErr(null);
    try {
      await api.addGroceryItem({ product_id: product.id, name: product.name });
      setAddedToList(true);
      setTimeout(() => setAddedToList(false), 2500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add to grocery list");
    } finally {
      setAddingToList(false);
    }
  }

  async function onDeleteProduct() {
    if (!product) return;
    if (
      !confirm(
        `Delete "${product.name}" and all its lots? This can't be undone.`
      )
    )
      return;
    try {
      await api.deleteProduct(product.id);
      navigate("/", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    }
  }

  async function onUpdateLot(
    lotId: string,
    updates: Partial<InventoryLot>
  ): Promise<void> {
    await api.updateLot(lotId, updates);
    await load();
  }

  async function onConsumeLot(lotId: string) {
    try {
      const r = await api.consumeLot(lotId, 1);
      // If the last lot of the only-product was deleted, navigate back; the
      // detail page is meaningless if the product has no lots and the user
      // probably wants to see the inventory list.
      if (r.deleted && lots.length === 1) {
        navigate("/", { replace: true });
        return;
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    }
  }

  async function onRemoveLot(lotId: string) {
    if (!confirm("Remove this lot?")) return;
    try {
      await api.deleteLot(lotId);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    }
  }

  async function onAddLot(input: {
    quantity: number;
    unit: string | null;
    best_before: string | null;
    location: string | null;
    notes: string | null;
  }) {
    if (!product) return;
    await api.addInventory({
      product_id: product.id,
      ...input,
    });
    await load();
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-stone-500 dark:text-stone-400">Loading…</div>
    );
  }
  if (!product) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <div className="card border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          {err ?? "Product not found."}
        </div>
      </div>
    );
  }

  const productDirty =
    productName !== product.name ||
    productBrand !== (product.brand ?? "") ||
    productCategory !== (product.category ?? "") ||
    productImage !== (product.image_url ?? "") ||
    parseFloat(productMinQty) !== product.min_quantity ||
    productStarred !== product.is_starred;

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
      >
        ← Back
      </button>

      <button
        type="button"
        onClick={() => setProductStarred((v) => !v)}
        className={`flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${
          productStarred
            ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            : "bg-stone-100 text-stone-600 hover:bg-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700"
        }`}
        aria-pressed={productStarred}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill={productStarred ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
          <path d="M12 2l2.39 4.84L20 7.62l-3.79 3.7.89 5.21L12 14.27l-5.1 2.26.89-5.21L4 7.62l5.61-.78L12 2z" strokeLinejoin="round" />
        </svg>
        {productStarred ? "Essential" : "Mark essential"}
      </button>

      {/* Product card */}
      <div className="card p-4">
        {productImage && (
          <img
            src={productImage}
            alt=""
            className="mb-4 h-24 w-24 rounded-lg border border-stone-200 object-cover dark:border-stone-700"
          />
        )}

        <div className="space-y-3">
          <div>
            <label className="label">Product name</label>
            <input
              className="input"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Brand</label>
            <input
              className="input"
              value={productBrand}
              onChange={(e) => setProductBrand(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Category</label>
            <input
              className="input"
              list="detail-category-suggestions"
              placeholder="Snacks, Dairy, Produce…"
              value={productCategory}
              onChange={(e) => setProductCategory(e.target.value)}
            />
            <datalist id="detail-category-suggestions">
              {suggestions.categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="label" htmlFor="min-qty">
              Restock when below
            </label>
            <div className="flex items-center gap-2">
              <input
                id="min-qty"
                type="number"
                min="0"
                step="any"
                className="input w-24"
                value={productMinQty}
                onChange={(e) => setProductMinQty(e.target.value)}
              />
              <span className="text-sm text-stone-500 dark:text-stone-400">
                — flag this product as "running low" when total drops below this number. Set to 0 to mute grocery list reminders for this item; to remove the product entirely, use the trash icon below.
              </span>
            </div>
          </div>
          {product.barcode && (
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Barcode: <span className="font-mono">{product.barcode}</span>
            </p>
          )}
        </div>

        {productDirty && (
          <button
            onClick={onSaveProduct}
            className="btn-primary mt-4 w-full"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        )}
      </div>

      {/* Lots */}
      <section>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-700 dark:text-stone-300">
          Lots
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-normal text-stone-500 dark:bg-stone-800 dark:text-stone-400">
            {lots.length}
          </span>
        </h2>
        {lots.length === 0 ? (
          <p className="rounded-lg border border-dashed border-stone-300 px-4 py-6 text-center text-sm text-stone-500 dark:border-stone-700 dark:text-stone-400">
            No lots in stock. Add one below.
          </p>
        ) : (
          <ul className="space-y-2">
            {lots.map((lot) => (
              <LotEditor
                key={lot.id}
                lot={lot}
                suggestions={suggestions}
                onUpdate={onUpdateLot}
                onConsume={onConsumeLot}
                onRemove={onRemoveLot}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Add another lot */}
      <AddLotForm
        suggestions={suggestions}
        defaultUnit={product.default_unit ?? ""}
        defaultLocation={lots[0]?.location ?? ""}
        onAdd={onAddLot}
      />

      {err && (
        <div className="card border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          {err}
        </div>
      )}

      <button
        type="button"
        onClick={onAddToList}
        disabled={addingToList || addedToList}
        className="btn-secondary w-full"
      >
        {addedToList
          ? "✓ Added to grocery list"
          : addingToList
          ? "Adding…"
          : "+ Add to grocery list"}
      </button>

      <button onClick={onDeleteProduct} className="btn-danger w-full">
        Delete this product
      </button>
    </div>
  );
}

// ---------- LotEditor: one lot, expandable for editing ----------

function LotEditor({
  lot,
  suggestions,
  onUpdate,
  onConsume,
  onRemove,
}: {
  lot: InventoryLot;
  suggestions: { locations: string[]; categories: string[] };
  onUpdate: (lotId: string, updates: Partial<InventoryLot>) => Promise<void>;
  onConsume: (lotId: string) => Promise<void>;
  onRemove: (lotId: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state seeded from the current lot. Reset when the lot prop changes
  // (after a parent reload).
  const [quantity, setQuantity] = useState(String(lot.quantity));
  const [unit, setUnit] = useState(lot.unit ?? "");
  const [bestBefore, setBestBefore] = useState(lot.best_before ?? "");
  const [location, setLocation] = useState(lot.location ?? "");
  const [notes, setNotes] = useState(lot.notes ?? "");

  useEffect(() => {
    setQuantity(String(lot.quantity));
    setUnit(lot.unit ?? "");
    setBestBefore(lot.best_before ?? "");
    setLocation(lot.location ?? "");
    setNotes(lot.notes ?? "");
  }, [lot]);

  const dirty =
    quantity !== String(lot.quantity) ||
    unit !== (lot.unit ?? "") ||
    bestBefore !== (lot.best_before ?? "") ||
    location !== (lot.location ?? "") ||
    notes !== (lot.notes ?? "");

  async function handleSave() {
    if (!dirty) return;
    setSaving(true);
    try {
      await onUpdate(lot.id, {
        quantity: parseFloat(quantity) || 0,
        unit: unit || null,
        best_before: bestBefore || null,
        location: location || null,
        notes: notes || null,
      });
      setExpanded(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="card p-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-medium tabular-nums">
            {lot.quantity}
            {lot.unit ? ` × ${lot.unit}` : ""}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-stone-500 dark:text-stone-400">
            <span>{formatBestBefore(lot.best_before)}</span>
            {lot.location && (
              <>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span>{lot.location}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void onConsume(lot.id)}
            className="btn-secondary px-2.5 !py-1.5 text-sm font-semibold tabular-nums"
            aria-label="Use one"
            title="Use one"
          >
            −1
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn-ghost !p-1.5"
            aria-label={expanded ? "Collapse" : "Edit lot"}
            title={expanded ? "Collapse" : "Edit lot"}
            aria-expanded={expanded}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => void onRemove(lot.id)}
            className="btn-ghost !p-1.5 text-stone-400 hover:text-red-600 dark:text-stone-500 dark:hover:text-red-400"
            aria-label="Remove lot"
            title="Remove lot"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-stone-200 pt-3 dark:border-stone-800">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Quantity</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Unit</label>
              <input
                className="input"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="label">Best before</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="input max-w-[14rem]"
                value={bestBefore}
                onChange={(e) => setBestBefore(e.target.value)}
              />
              {bestBefore && (
                <button
                  type="button"
                  onClick={() => setBestBefore("")}
                  className="rounded-md px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100"
                  aria-label="Clear expiry date"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="label">Location</label>
            <input
              className="input"
              list={`lot-${lot.id}-locations`}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
            <datalist id={`lot-${lot.id}-locations`}>
              {suggestions.locations.map((l) => (
                <option key={l} value={l} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input min-h-[50px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="btn-secondary flex-1"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary flex-1"
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Save lot"}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------- AddLotForm: collapsible "+ Add another lot" form ----------

function AddLotForm({
  suggestions,
  defaultUnit,
  defaultLocation,
  onAdd,
}: {
  suggestions: { locations: string[]; categories: string[] };
  defaultUnit: string;
  defaultLocation: string;
  onAdd: (input: {
    quantity: number;
    unit: string | null;
    best_before: string | null;
    location: string | null;
    notes: string | null;
  }) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState(defaultUnit);
  const [bestBefore, setBestBefore] = useState("");
  const [location, setLocation] = useState(defaultLocation);
  const [notes, setNotes] = useState("");

  function reset() {
    setQuantity("1");
    setUnit(defaultUnit);
    setBestBefore("");
    setLocation(defaultLocation);
    setNotes("");
    setErr(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await onAdd({
        quantity: parseFloat(quantity) || 1,
        unit: unit.trim() || null,
        best_before: bestBefore || null,
        location: location.trim() || null,
        notes: notes.trim() || null,
      });
      reset();
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add lot");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="card flex w-full items-center justify-center gap-2 border-dashed py-3 text-sm font-medium text-stone-600 hover:border-brand-500 hover:text-brand-700 dark:text-stone-300 dark:hover:border-brand-500 dark:hover:text-brand-400"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
        </svg>
        Add another lot
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card space-y-3 p-4">
      <h3 className="text-sm font-semibold text-stone-700 dark:text-stone-300">
        New lot
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Quantity</label>
          <input
            type="number"
            min="0"
            step="any"
            className="input"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Unit</label>
          <input
            className="input"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
        </div>
      </div>
      <div>
        <label className="label">Best before</label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="input max-w-[14rem]"
            value={bestBefore}
            onChange={(e) => setBestBefore(e.target.value)}
          />
          {bestBefore && (
            <button
              type="button"
              onClick={() => setBestBefore("")}
              className="rounded-md px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100"
              aria-label="Clear expiry date"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <div>
        <label className="label">Location</label>
        <input
          className="input"
          list="add-lot-locations"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
        <datalist id="add-lot-locations">
          {suggestions.locations.map((l) => (
            <option key={l} value={l} />
          ))}
        </datalist>
      </div>
      <div>
        <label className="label">Notes</label>
        <textarea
          className="input min-h-[50px]"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          className="btn-secondary flex-1"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary flex-1"
          disabled={saving}
        >
          {saving ? "Adding…" : "Add lot"}
        </button>
      </div>
    </form>
  );
}
